import React from "react"
import { Radio } from "antd"
import { useTabSpecStore } from "../../runtime/tabSpecStore"
import { SortRule } from "../../types/tabSpecDocument"
import { sortChoiceText } from "../Grid/describe"
import Field from "./Field.component"
import { useInspectedTable } from "./useInspectedTable"

/** A table's order: the defaults', or its own. */
type SortChoice = "default" | SortRule
/** A table's empty rows: as the defaults say, or shown or hidden whatever they say. */
type HideChoice = "default" | "show" | "hide"

/** What the rows of the table are, in the words of the settings. */
export type RowWords = "answers" | "grid rows" | "ranges"

/** The words of each setting for each kind of row. */
const WORDS: Record<RowWords, { sort: string; hide: string }> = {
  answers: { sort: "Sort answers", hide: "Answers nobody gave" },
  "grid rows": { sort: "Sort grid rows", hide: "Grid rows nobody answered" },
  ranges: { sort: "Sort ranges", hide: "Ranges nobody is in" },
}

const SORT_HINT =
  "Sorted by the Totals column, within the rows between nets and statistics. Exceptions and answers kept at the end stay at the end."

/**
 * The order of a table's rows and whether it shows those nobody gave, each
 * following the defaults or the table's own. `sortable`: the rows can be
 * sorted (the ranges of a number and the yes or no rows cannot).
 * `fromData`: the rows follow the data, not a questionnaire.
 */
const RowOrder: React.FC<{
  tableId: string
  rows: RowWords
  sortable: boolean
  fromData?: boolean
}> = ({ tableId, rows, sortable, fromData = false }) => {
  const inspected = useInspectedTable(tableId)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)
  if (!inspected) return null
  const { table, resolved } = inspected
  const defaults = inspected.document.defaults
  const words = WORDS[rows]
  const sortChoices: { value: SortChoice; label: string }[] = [
    { value: "default", label: `Follow the defaults (${sortChoiceText(defaults.sort ?? "none", fromData)})` },
    { value: "none", label: sortChoiceText("none", fromData) },
    { value: "desc", label: sortChoiceText("desc") },
  ]
  const hideChoices: { value: HideChoice; label: string }[] = [
    { value: "default", label: `Follow the defaults (${defaults.hideEmpty ? "hidden" : "shown"})` },
    { value: "show", label: "Show" },
    { value: "hide", label: "Hide" },
  ]
  const hidden: HideChoice =
    table.hideEmpty === undefined ? "default" : table.hideEmpty ? "hide" : "show"
  return (
    <>
      {sortable && (
        <Field label={words.sort}>
          <Radio.Group
            className="ts-base__choices"
            aria-label={words.sort}
            name={`ts-sort-${tableId}`}
            value={table.sort ?? "default"}
            options={sortChoices}
            onChange={(e) => {
              const choice = e.target.value as SortChoice
              if (choice === "default") clearOverrides(tableId, ["sort"])
              else updateTable(tableId, { sort: choice })
            }}
          />
          {resolved.sort === "desc" && <p className="ts-hint">{SORT_HINT}</p>}
        </Field>
      )}
      <Field label={words.hide}>
        <Radio.Group
          className="ts-base__choices"
          aria-label={words.hide}
          name={`ts-hide-empty-${tableId}`}
          value={hidden}
          options={hideChoices}
          onChange={(e) => {
            const choice = e.target.value as HideChoice
            if (choice === "default") clearOverrides(tableId, ["hideEmpty"])
            else updateTable(tableId, { hideEmpty: choice === "hide" })
          }}
        />
      </Field>
    </>
  )
}

export default RowOrder
